import { TextField } from '@components/TextField';
import { IOption } from '@components/Filter/filter.utils';
import Colors from '@constants/Colors';
import { CircularProgress, InputAdornment } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '../AutoComplete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface InputAutocompleteProps {
    id?: string;
    name: string;
    allowCreate?: boolean; // Allow creating new options
    placeholder?: string;
    label?: string; // Label for the TextField
    multiple?: boolean;
    options?: IOption[]; // Static options
    groupBy?: (option: IOption) => string | undefined; // Function to group options
    loading?: boolean; // Loading state
    noOptionsText?: string; // Custom text when no options are available
    loadingText?: React.ReactNode; // Custom loading text
    onChange?: (value: string | string[]) => void;
    [key: string]: any; // Allow additional props
}

const InputAutocomplete: React.FC<InputAutocompleteProps> = ({
    id,
    name,
    placeholder,
    label,
    multiple = false,
    options: optionsProp = [],
    groupBy,
    loading = false,
    noOptionsText = 'Aucun résultat',
    loadingText = <CircularProgress size={24} color="inherit" />,
    allowCreate = false,
    size = 'small',
    onChange,
    ...rest
}) => {
    const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false); // State to manage dropdown visibility
    const [options, setOptions] = useState<IOption[]>(optionsProp);
    const { control, watch, setValue } = useFormContext();
    const fieldValue = watch(name);

    React.useEffect(() => {
        setOptions(optionsProp);
    }, [optionsProp]);

    const handleOpen = useCallback(() => {
        setIsOptionsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setIsOptionsOpen(false);
    }, []);

    const handleEndAdornmentClick = useCallback(() => {
        setIsOptionsOpen(prev => !prev);
    }, []);

    // Helper to check if input value exists in options
    const findOptionByLabel = (label: string) =>
        options.find(opt => opt.label.toLowerCase() === label.toLowerCase());

    // Helper to get selected options from field value
    const getSelectedOptions = () => {
        if (!fieldValue) return multiple ? [] : null;

        if (multiple) {
            if (!Array.isArray(fieldValue)) return [];
            const selectedOptions = fieldValue.map(value => {
                const found = options.find(option => option.value === value);
                return found;
            }).filter(Boolean);
            return selectedOptions;
        } else {
            const found = options.find(option => option.value === fieldValue);
            return found || null;
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Autocomplete
                    {...field}
                    id={id}
                    multiple={multiple}
                    options={options}
                    value={getSelectedOptions() as any}
                    groupBy={groupBy}
                    getOptionLabel={(option: IOption) =>
                        groupBy && option.group ? `${option.group} - ${option.label}` : option.label
                    }
                    fullWidth
                    onChange={(_, value, reason, details) => {
                        // If allowCreate is true and value is a string (freeSolo), add it to options
                        if (allowCreate && reason === 'createOption') {
                            let newOption: IOption;
                            if (typeof value === 'string') {
                                newOption = { label: value, value };
                            } else if (Array.isArray(value)) {
                                // For multiple, value is array
                                const last = value[value.length - 1];
                                if (typeof last === 'string') {
                                    newOption = { label: last, value: last };
                                    setOptions(prev => [...prev, newOption]);
                                    const newValues = [...value.slice(0, -1), newOption].map(item =>
                                        typeof item === 'object' ? item.value : item
                                    );
                                    setValue(name, newValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    field.onChange(newValues);
                                    onChange?.(newValues);
                                    return;
                                }
                            }
                            if (newOption && !findOptionByLabel(newOption.label)) {
                                setOptions(prev => [...prev, newOption]);
                                setValue(name, newOption.value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                field.onChange(newOption.value);
                                onChange?.(newOption.value);
                                return;
                            }
                        }

                        // Handle regular value changes
                        if (multiple) {
                            // For multiple selection, extract values from option objects
                            const selectedValues = Array.isArray(value)
                                ? value.map(item => typeof item === 'object' && item !== null ? item.value : item).filter(Boolean)
                                : [];
                            setValue(name, selectedValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            field.onChange(selectedValues);
                            if (reason !== 'clear') onChange?.(selectedValues);
                        } else {
                            // For single selection
                            const selectedValue = typeof value === 'object' && value !== null ? (value as IOption)?.value : value;
                            setValue(name, selectedValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            field.onChange(selectedValue);
                            if (reason !== 'clear') onChange?.(selectedValue as string);
                        }

                        if (reason === 'clear') {
                            setValue(name, multiple ? [] : '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            field.onChange(multiple ? [] : '');
                            onChange?.(multiple ? [] : '');
                            handleClose();
                        }
                    }}
                    isOptionEqualToValue={(option: IOption, value: IOption) => option.value === value.value}
                    filterSelectedOptions
                    loading={loading}
                    loadingText={loadingText}
                    noOptionsText={noOptionsText}
                    open={multiple ? isOptionsOpen : undefined}
                    onOpen={handleOpen}
                    onClose={(_, reason) => {
                        if (multiple && reason === 'selectOption') return;
                        handleClose();
                    }}
                    popupIcon={
                        isOptionsOpen ? (
                            <i
                                className="fi fi-br-check"
                                onClick={handleEndAdornmentClick}
                                style={{ color: Colors.PRIMARY, fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                            />
                        ) : undefined
                    }
                    slotProps={{
                        popupIndicator: {
                            style: {
                                transform: 'none',
                            },
                        },
                    }}
                    ChipProps={{
                        deleteIcon: <i className="fi fi-br-cross-small" style={{ fontSize: 12 }} />,
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            size={size}
                            label={label}
                            placeholder={placeholder || 'Tapez pour rechercher...'}
                            error={!!error}
                            helperText={error ? error.message : ''}
                            variant="outlined"
                            sx={{
                                background: theme => theme.palette.dashboard.main,
                            }}
                        // InputProps={{
                        //     ...params.InputProps,
                        //     endAdornment: allowCreate ? (
                        //         <InputAdornment position="end">
                        //             <ArrowDropDownIcon />
                        //         </InputAdornment>
                        //     ) : undefined,
                        // }}
                        />
                    )}
                    sx={{
                        background: theme => theme.palette.dashboard.main,
                    }}
                    freeSolo={allowCreate}
                    selectOnFocus={allowCreate}
                    handleHomeEndKeys={allowCreate}
                    {...rest}
                />
            )}
        />
    );
};

export default InputAutocomplete;
